import React, { useMemo } from "react";
import { Send, CalendarClock } from "lucide-react";
import DateTimePicker from "modules/swiftboard/components/ui/DateTimePicker";
import { useSettings } from "context/SettingsContext";
import { resolveDatePrefs, getTzParts } from "services/date-converter";

const OPTIONS = [
  { value: "NOW", label: "Send Now", desc: "Starts sending as soon as you finish this wizard.", icon: Send },
  { value: "SCHEDULED", label: "Schedule for later", desc: "Pick a future date & time — it'll fire automatically.", icon: CalendarClock },
];

const StepSchedule = ({ sendMode, setSendMode, scheduledAt, setScheduledAt, timeZone }) => {
  const { settings } = useSettings() || {};
  const { timeFormat } = resolveDatePrefs(settings);

  const minDate = useMemo(() => {
    const p = getTzParts(new Date(), timeZone);
    return new Date(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  }, [timeZone]);

  const offsetLabel = useMemo(() => {
    try {
      const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName");
      return part?.value || "";
    } catch {
      return "";
    }
  }, [timeZone]);

  const isPast = sendMode === "SCHEDULED" && scheduledAt && scheduledAt.getTime() < minDate.getTime();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = sendMode === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => setSendMode(opt.value)}
              className={`flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left ${
                selected ? "border-primary bg-primary_tint" : "border-border bg-surface"
              }`}
            >
              <Icon size={17} className={`flex-shrink-0 mt-0.5 ${selected ? "text-primary" : "text-text_muted"}`} />
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${selected ? "text-primary" : "text-text_primary"}`}>{opt.label}</div>
                <div className="text-[11px] text-text_secondary mt-0.5">{opt.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {sendMode === "SCHEDULED" && (
        <div>
          <label className="block text-xs font-medium text-text_secondary mb-1.5">Send Date & Time</label>
          <DateTimePicker
            value={scheduledAt}
            onChange={setScheduledAt}
            minDate={minDate}
            timeFormat={timeFormat}
            placeholder="Select when this campaign should send"
            hasError={isPast}
          />
          {isPast ? (
            <p className="text-[11px] text-danger mt-1">Scheduled time must be in the future.</p>
          ) : (
            <p className="text-[11px] text-text_muted mt-1.5">
              Times shown in {timeZone}{offsetLabel ? ` (${offsetLabel})` : ""} — your account timezone.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StepSchedule;
